import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  Link,
  Divider,
  useTheme,
  IconButton,
  alpha
} from '@mui/material';
import {
  Instagram,
  YouTube,
  X as XIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

// Custom Icons
const TikTokIcon = (props: any) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const CustomXIcon = (props: any) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export function Footer() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToFeatures = () => {
    if (location.pathname === '/') {
      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <Box component="footer" sx={{ py: 4, bgcolor: 'background.paper', borderTop: `1px solid ${theme.palette.divider}` }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>

          {/* Logo & Tagline */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box component="img" src="/logo.svg" alt="ContentSignal Logo" sx={{ width: 32, height: 32 }} />
            <Typography variant="h6" fontWeight="bold">
              ContentSignal
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
            Helping creators decide what to post — with confidence.
          </Typography>

          {/* Navigation Links - Centered Row */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: { xs: 2.5, sm: 4 },
              mb: 3
            }}
          >
            <Link
              component="button"
              color="text.primary"
              underline="none"
              variant="subtitle2"
              onClick={scrollToFeatures}
              sx={{ fontWeight: 600, '&:hover': { color: 'primary.main' } }}
            >
              Features
            </Link>
            <Link
              component="button"
              color="text.primary"
              underline="none"
              variant="subtitle2"
              onClick={() => navigate('/pricing')}
              sx={{ fontWeight: 600, '&:hover': { color: 'primary.main' } }}
            >
              Pricing
            </Link>
            <Link
              component="button"
              color="text.primary"
              underline="none"
              variant="subtitle2"
              onClick={() => navigate('/about')}
              sx={{ fontWeight: 600, '&:hover': { color: 'primary.main' } }}
            >
              About
            </Link>
            <Link
              component="button"
              color="text.primary"
              underline="none"
              variant="subtitle2"
              onClick={() => navigate('/privacy')}
              sx={{ fontWeight: 600, '&:hover': { color: 'primary.main' } }}
            >
              Privacy
            </Link>
            <Link
              component="button"
              color="text.primary"
              underline="none"
              variant="subtitle2"
              onClick={() => navigate('/terms')}
              sx={{ fontWeight: 600, '&:hover': { color: 'primary.main' } }}
            >
              Terms
            </Link>
            <Link
              href="mailto:support@contentsignal.io"
              color="text.primary"
              underline="none"
              variant="subtitle2"
              sx={{ fontWeight: 600, '&:hover': { color: 'primary.main' } }}
            >
              Contact
            </Link>
          </Box>

          {/* Social Icons */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <IconButton
              aria-label="Instagram"
              component="a"
              href="https://instagram.com/contentsignalapp"
              target="_blank"
              rel="noopener"
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
            >
              <Instagram fontSize="medium" />
            </IconButton>

            <IconButton
              aria-label="X (Twitter)"
              component="a"
              href="https://x.com/ContentSig33034"
              target="_blank"
              rel="noopener"
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
            >
              <CustomXIcon />
            </IconButton>

            <IconButton
              aria-label="TikTok"
              component="a"
              href="https://tiktok.com/@contentsignal"
              target="_blank"
              rel="noopener"
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
            >
              <TikTokIcon />
            </IconButton>

            <IconButton
              aria-label="YouTube"
              component="a"
              href="https://youtube.com"
              target="_blank"
              rel="noopener"
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
            >
              <YouTube fontSize="medium" />
            </IconButton>
          </Stack>

          {/* Copyright */}
          <Typography variant="caption" color="text.disabled">
            © {new Date().getFullYear()} ContentSignal. All rights reserved.
          </Typography>

        </Box>
      </Container>
    </Box>
  );
}